import { useState } from "react";
import { Link } from "react-router-dom";
import {
  IdCard, Sparkles, CreditCard, Briefcase, FileText, CalendarDays,
  Video, Users, User, BarChart3, Pill,
  Smartphone, PieChart, CalendarCheck, ArrowRight, RefreshCw,
} from "lucide-react";
import { useLang } from "@/contexts/LangContext";

type TabKey = "admin" | "care" | "grow";

type Item = { icon: React.ComponentType<{ className?: string }>; label: string; labelBn: string; to: string };

type Tab = {
  key: TabKey;
  pill: { en: string; bn: string };
  title: { en: string; bn: string };
  desc: { en: string; bn: string };
  items: Item[];
  bg: string; // panel bg
  chip: string; // active chip bg
  chipText: string; // active chip text
  photoBg: string; // photo card bg
  overlay: React.ReactNode;
  photo: string; // gradient placeholder
};

const TABS: Tab[] = [
  {
    key: "admin",
    pill: { en: "Streamline admin tasks", bn: "প্রশাসনিক কাজ সহজ করুন" },
    title: { en: "Streamline admin tasks", bn: "প্রশাসনিক কাজ সহজ করুন" },
    desc: {
      en: "From scheduling and billing to documentation, DoctorsX helps you manage the most time-consuming parts of your practice.",
      bn: "শিডিউল, বিলিং থেকে ডকুমেন্টেশন — DoctorsX আপনার চেম্বারের সময়সাপেক্ষ কাজগুলো সামলায়।",
    },
    items: [
      { icon: IdCard, label: "BMDC verification", labelBn: "BMDC যাচাই", to: "/features" },
      { icon: Sparkles, label: "AI ambient scribe", labelBn: "AI অ্যাম্বিয়েন্ট স্ক্রাইব", to: "/ai/ambient-scribe" },
      { icon: CreditCard, label: "bKash & card billing", labelBn: "বিকাশ ও কার্ড বিলিং", to: "/features" },
      { icon: Briefcase, label: "Multi-chamber setup", labelBn: "একাধিক চেম্বার", to: "/features" },
      { icon: FileText, label: "Bilingual prescriptions", labelBn: "দ্বিভাষিক প্রেসক্রিপশন", to: "/features" },
      { icon: CalendarDays, label: "Smart scheduling", labelBn: "স্মার্ট শিডিউলিং", to: "/features" },
    ],
    bg: "#e9f7ec",
    chip: "#bfe6c5",
    chipText: "#0f3d1e",
    photoBg: "linear-gradient(135deg, #7cc4ff 0%, #b7e0ff 100%)",
    overlay: (
      <div className="absolute top-5 left-5 bg-white rounded-xl shadow-lg p-3 pr-4 max-w-[240px]">
        <div className="flex items-center justify-between">
          <span className="text-[13px] font-semibold text-[#0f172a]">Availability block</span>
          <span className="text-[#94a3b8] text-sm">×</span>
        </div>
        <div className="mt-2 inline-flex items-center gap-1.5 bg-[#fde68a] text-[#7c4a03] text-[11px] font-semibold px-2.5 py-1 rounded-md">
          <RefreshCw className="w-3 h-3" /> Every week on Tue, Thu
        </div>
      </div>
    ),
    photo: "🩺",
  },
  {
    key: "care",
    pill: { en: "Improve patient care", bn: "রোগী সেবা উন্নত করুন" },
    title: { en: "Improve patient care", bn: "রোগীর সেবা উন্নত করুন" },
    desc: {
      en: "Track patient progress, manage medication, and offer a seamless care experience with an all-in-one EHR.",
      bn: "রোগীর অগ্রগতি ট্র্যাক করুন, ওষুধ পরিচালনা করুন এবং নিরবচ্ছিন্ন সেবা দিন।",
    },
    items: [
      { icon: Video, label: "Telehealth video visits", labelBn: "টেলিহেলথ ভিডিও ভিজিট", to: "/features" },
      { icon: Users, label: "Family & group visits", labelBn: "পরিবার ও গ্রুপ ভিজিট", to: "/features" },
      { icon: User, label: "Patient portal", labelBn: "রোগী পোর্টাল", to: "/features" },
      { icon: BarChart3, label: "Vitals & risk trends", labelBn: "ভাইটালস ও ঝুঁকি ট্রেন্ড", to: "/features" },
      { icon: Pill, label: "e-Prescribing", labelBn: "ই-প্রেসক্রাইব", to: "/features" },
    ],
    bg: "#fdeae4",
    chip: "#f2a99a",
    chipText: "#4a1a10",
    photoBg: "linear-gradient(135deg, #f6c8bc 0%, #f8d9cd 100%)",
    overlay: (
      <div className="absolute top-6 left-6 flex flex-col gap-2">
        {[0, 1, 2].map((i) => (
          <div key={i} className="w-16 h-12 rounded-lg bg-white shadow-md border border-white/60"
            style={{ background: `linear-gradient(135deg, #${["fbcfe8", "fde68a", "bfdbfe"][i]}, #fff)` }} />
        ))}
      </div>
    ),
    photo: "👩‍⚕️",
  },
  {
    key: "grow",
    pill: { en: "Manage & grow your practice", bn: "চেম্বার পরিচালনা ও বৃদ্ধি" },
    title: { en: "Manage & grow your practice", bn: "চেম্বার পরিচালনা ও বৃদ্ধি করুন" },
    desc: {
      en: "Unlock growth with easy-to-use tools. Operate solo or group practices, generate revenue reports, and more.",
      bn: "সহজ টুল দিয়ে প্র্যাকটিস বাড়ান — একক বা গ্রুপ, রিপোর্ট ও আরও অনেক কিছু।",
    },
    items: [
      { icon: Smartphone, label: "Mobile & desktop apps", labelBn: "মোবাইল ও ডেস্কটপ অ্যাপ", to: "/features" },
      { icon: PieChart, label: "Analytics & reports", labelBn: "অ্যানালিটিক্স ও রিপোর্ট", to: "/features" },
      { icon: CalendarCheck, label: "Online appointment requests", labelBn: "অনলাইন অ্যাপয়েন্টমেন্ট", to: "/features" },
    ],
    bg: "#dbeeff",
    chip: "#9dd0ff",
    chipText: "#0a2540",
    photoBg: "linear-gradient(135deg, #fca5a5 0%, #fed7aa 100%)",
    overlay: (
      <div className="absolute bottom-5 right-5 bg-[#fde68a] rounded-lg shadow-lg p-3 min-w-[180px]">
        <div className="text-[11px] font-semibold text-[#0f172a]">Outstanding balances</div>
        <div className="text-lg font-bold text-[#0f172a]">৳ 12,900</div>
        <div className="mt-2 flex items-center justify-between text-[10px] text-[#7c4a03]"><span>PATIENTS</span><span>৳ 8,500</span></div>
        <div className="h-px bg-[#f59e0b]/40 my-1" />
        <div className="flex items-center justify-between text-[10px] text-[#7c4a03]"><span>INSURANCE</span><span>৳ 4,400</span></div>
      </div>
    ),
    photo: "💼",
  },
];

export default function IntuitiveToolsSection() {
  const { lang } = useLang();
  const isBn = lang === "bn";
  const [active, setActive] = useState<TabKey>("admin");
  const tab = TABS.find((t) => t.key === active)!;

  const displayFont = isBn
    ? "'Noto Sans Bengali', 'Plus Jakarta Sans', system-ui, sans-serif"
    : "'Plus Jakarta Sans', 'Inter', system-ui, sans-serif";

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
      <div
        className="rounded-[32px] p-6 sm:p-10 lg:p-14 transition-colors duration-500"
        style={{ background: tab.bg }}
      >
        <h2
          className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-[#0f172a] tracking-tight"
          style={{ fontFamily: displayFont }}
        >
          {isBn ? "সহজ টুল, যা আপনাকে এগিয়ে রাখে" : "Intuitive tools to help you thrive"}
        </h2>

        {/* Tab pill switcher */}
        <div className="mt-8 flex justify-center">
          <div className="bg-white rounded-full p-1.5 shadow-sm flex flex-wrap gap-1 max-w-full">
            {TABS.map((t) => {
              const isActive = t.key === active;
              return (
                <button
                  key={t.key}
                  onClick={() => setActive(t.key)}
                  className="px-4 sm:px-5 py-2.5 rounded-full text-[13px] sm:text-sm font-semibold transition-colors"
                  style={{
                    background: isActive ? t.chip : "transparent",
                    color: isActive ? t.chipText : "#334155",
                  }}
                >
                  {isBn ? t.pill.bn : t.pill.en}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="mt-10 grid gap-8 lg:grid-cols-2 items-center">
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-[#0f172a]" style={{ fontFamily: displayFont }}>
              {isBn ? tab.title.bn : tab.title.en}
            </h3>
            <p className="mt-3 text-[15px] text-[#334155] leading-relaxed max-w-md">
              {isBn ? tab.desc.bn : tab.desc.en}
            </p>

            <ul className="mt-6 space-y-3">
              {tab.items.map(({ icon: Icon, label, labelBn, to }) => (
                <li key={label}>
                  <Link
                    to={to}
                    className="inline-flex items-center gap-2.5 text-[15px] font-semibold text-[#0f172a] hover:text-[#1ba179] group"
                  >
                    <Icon className="w-4 h-4 text-[#0f172a]/70 group-hover:text-[#1ba179]" />
                    <span className="underline underline-offset-4 decoration-[#0f172a]/30 group-hover:decoration-[#1ba179]">
                      {isBn ? labelBn : label}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>

            <Link
              to="/features"
              className="mt-8 inline-flex items-center gap-2 bg-[#0b2e2a] hover:bg-[#0f3d38] text-white text-[14px] font-semibold px-6 py-3.5 rounded-full transition-colors"
            >
              {isBn ? "সব ফিচার দেখুন" : "Explore all features"}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Right visual */}
          <div className="relative">
            <div
              className="relative rounded-3xl aspect-[4/5] sm:aspect-[5/5] w-full max-w-md ml-auto shadow-xl overflow-hidden"
              style={{ background: tab.photoBg }}
            >
              <div className="absolute inset-0 flex items-center justify-center text-[9rem] opacity-40 select-none">
                {tab.photo}
              </div>
              {tab.overlay}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
